/**
 * useAnchoredMenu — keeps a cursor-anchored floating menu inside the viewport.
 *
 * Canvas context menus open at the clicked element, which works in the middle of
 * the screen and breaks everywhere else. Right-clicking a node near the bottom
 * ran the menu off the edge: the last few actions (Pin, Stash, View Details)
 * were simply unreachable, with no scroll and no reposition.
 *
 * Both menus got there differently. `NodeContextMenu` tried to compensate with
 * hard-coded guesses — `if (y > innerHeight - 350) top = y - 300` — where the
 * real menu is ~490px tall and *varies* with which entries are shown (cluster,
 * convergence, and multi-select each render a different list), so the threshold
 * and the shift were both wrong, and the shift could push the menu off the top
 * instead. `EdgeContextMenu` had no viewport handling at all.
 *
 * The fix is to stop guessing: render, measure, then place. Flip to the other
 * side of the anchor when the preferred side does not fit, clamp into the
 * viewport, and cap height with an internal scroll when the menu is taller than
 * any available space — which is reachable on a short window, where no amount of
 * flipping helps.
 */
import { useLayoutEffect, useRef, useState } from 'react';

export interface MenuAnchor {
    x: number;
    y: number;
}

/** Natural size of the menu, independent of any cap we have already applied. */
export interface MenuSize {
    width: number;
    height: number;
}

export interface Viewport {
    width: number;
    height: number;
}

export interface MenuPlacement {
    top: number;
    left: number;
    /** Set only when the menu cannot fit un-scrolled on either side. */
    maxHeight?: number;
}

export interface MenuInsets {
    /** Reserved chrome at the top (Workbench header). */
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

export interface AnchoredMenuOptions {
    /** Gap kept between the menu and the usable viewport edge. */
    pad?: number;
    /** Extra insets for fixed chrome (header / inspector) so the menu never
     *  opens under opaque UI the officer cannot click through. */
    insets?: MenuInsets;
}

const DEFAULT_PAD = 8;
/** Workbench header row stack — keep menus clear of Trace/Investigate chrome. */
export const WORKBENCH_MENU_TOP_INSET = 96;

function samePlacement(a: MenuPlacement | null, b: MenuPlacement): boolean {
    return a !== null && a.top === b.top && a.left === b.left && a.maxHeight === b.maxHeight;
}

function clamp(value: number, min: number, max: number): number {
    // max can fall below min on a viewport smaller than the menu; min wins so we
    // stay anchored to the top-left edge rather than jumping to a negative value.
    return Math.max(min, Math.min(value, Math.max(min, max)));
}

/**
 * Pure placement maths, exported for testing — the DOM parts of this hook are
 * awkward to drive in jsdom, and this is where the actual bug lived.
 *
 * Vertical: open downward from the anchor; flip to open upward when the menu
 * does not fit below; if it fits neither way, take the roomier side and cap
 * with internal scroll. Insets shrink the usable viewport so menus never hide
 * under the Workbench header.
 * Horizontal: same, opening rightward by preference.
 */
export function computeMenuPlacement(
    anchor: MenuAnchor,
    size: MenuSize,
    viewport: Viewport,
    pad: number = DEFAULT_PAD,
    insets: MenuInsets = {},
): MenuPlacement {
    const topInset = Math.max(0, insets.top ?? 0);
    const bottomInset = Math.max(0, insets.bottom ?? 0);
    const leftInset = Math.max(0, insets.left ?? 0);
    const rightInset = Math.max(0, insets.right ?? 0);

    const minTop = pad + topInset;
    const maxBottom = viewport.height - pad - bottomInset;
    const minLeft = pad + leftInset;
    const maxRight = viewport.width - pad - rightInset;

    // Usable free space relative to the anchor, after chrome insets.
    const roomBelow = Math.max(0, maxBottom - anchor.y);
    const roomAbove = Math.max(0, anchor.y - minTop);

    let top: number;
    let maxHeight: number | undefined;

    if (size.height <= roomBelow) {
        // Preferred: grow downward from the cursor / node.
        top = anchor.y;
    } else if (size.height <= roomAbove) {
        // Full menu fits above — flip up.
        top = anchor.y - size.height;
    } else if (roomBelow >= roomAbove) {
        // Neither side fits the full height. Prefer the roomier side and scroll.
        // Critical for top-half anchors: roomBelow is usually larger, so we stay
        // anchored at the click and scroll inside the menu instead of flipping
        // into the header with unreachable items.
        top = anchor.y;
        maxHeight = roomBelow;
    } else {
        maxHeight = roomAbove;
        top = anchor.y - maxHeight;
    }

    const effectiveHeight = maxHeight ?? size.height;
    top = clamp(top, minTop, Math.max(minTop, maxBottom - effectiveHeight));

    let left = anchor.x;
    if (left + size.width > maxRight) {
        left = anchor.x - size.width;
    }
    left = clamp(left, minLeft, Math.max(minLeft, maxRight - size.width));

    return maxHeight === undefined ? { top, left } : { top, left, maxHeight };
}

/**
 * Returns a ref to attach to the menu root and the style to spread onto it.
 *
 * The menu is rendered invisibly for one frame so it can be measured at its
 * natural size, matching how `InfoHint` and `TourSpotlight` avoid a visible
 * jump on first paint.
 */
export function useAnchoredMenu<T extends HTMLElement = HTMLDivElement>(
    anchor: MenuAnchor,
    options: AnchoredMenuOptions = {},
): { ref: React.RefObject<T | null>; style: React.CSSProperties } {
    const pad = options.pad ?? DEFAULT_PAD;
    const insets = options.insets;
    const insetTop = insets?.top ?? 0;
    const insetRight = insets?.right ?? 0;
    const insetBottom = insets?.bottom ?? 0;
    const insetLeft = insets?.left ?? 0;
    const ref = useRef<T | null>(null);
    const [placement, setPlacement] = useState<MenuPlacement | null>(null);

    // Callers pass a fresh object literal every render (`position` comes straight
    // off a click event), so the effect keys off the coordinates rather than the
    // object identity — otherwise it would tear down its listeners each render.
    const { x, y } = anchor;

    useLayoutEffect(() => {
        const measureAndPlace = () => {
            const el = ref.current;
            if (!el) return;
            // scrollHeight rather than the bounding rect: once a maxHeight cap is
            // applied the rect reports the capped height, which would feed back
            // into the next measurement and let the menu shrink on every resize.
            const size: MenuSize = { width: el.offsetWidth, height: el.scrollHeight };
            const next = computeMenuPlacement(
                { x, y },
                size,
                { width: window.innerWidth, height: window.innerHeight },
                pad,
                {
                    top: insetTop,
                    right: insetRight,
                    bottom: insetBottom,
                    left: insetLeft,
                },
            );
            // The capture-phase scroll listener below fires for every scrolling
            // container in the app, so bail out when the result is unchanged
            // rather than re-rendering the menu on each scroll event.
            setPlacement(prev => (samePlacement(prev, next) ? prev : next));
        };

        measureAndPlace();
        window.addEventListener('resize', measureAndPlace);
        // Capture phase: the canvas and inspector scroll in their own containers,
        // and the anchor is in viewport coordinates.
        window.addEventListener('scroll', measureAndPlace, true);
        return () => {
            window.removeEventListener('resize', measureAndPlace);
            window.removeEventListener('scroll', measureAndPlace, true);
        };
    }, [x, y, pad, insetTop, insetRight, insetBottom, insetLeft]);

    const style: React.CSSProperties = placement
        ? {
            position: 'fixed',
            top: placement.top,
            left: placement.left,
            // Always allow vertical scroll when capped — never rely on a class
            // `overflow-hidden` sibling (shorthand can fight inline overflow-y).
            maxHeight: placement.maxHeight,
            overflowY: placement.maxHeight === undefined ? 'visible' : 'auto',
            overflowX: 'hidden',
            visibility: 'visible',
        }
        : {
            // First frame: measure off-screen with opacity 0 (not visibility:hidden).
            // visibility:hidden + a missed measure left menus permanently invisible.
            position: 'fixed',
            top: y,
            left: x,
            opacity: 0,
            pointerEvents: 'none',
        };

    return { ref, style };
}
